import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { Close, CheckCircle, Cancel } from '@mui/icons-material';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

interface ApplicationData {
  id: string;
  tenantFirstName: string;
  tenantLastName: string;
  tenantEmail: string;
  tenantPhoneNumber?: string;
  apartmentNumber: string;
  address: string;
  city: string;
  contractStartDate?: string;
  contractEndDate?: string;
  rentalFee?: number;
  deposit?: number;
  creditCheckAnswers?: {
    betalningsanmarkningar: 'ja' | 'nej';
    skuldsaldo: 'ja' | 'nej';
    inkomst: 'ja' | 'nej';
  };
  creditCheckPdfUrl?: string;
  creditCheckComments?: string;
  creditCheckDate?: string;
}

type OwnerDecision = 'create_contract' | 'find_new_tenant' | 'cancel_application';

interface OwnerDecisionModalProps {
  open: boolean;
  onClose: () => void;
  application: ApplicationData;
  onDecisionMade?: (decision: OwnerDecision) => void;
}

const OwnerDecisionModal: React.FC<OwnerDecisionModalProps> = ({
  open,
  onClose,
  application,
  onDecisionMade
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [decisionMade, setDecisionMade] = useState<OwnerDecision | null>(null);

  const handleDecision = async (decision: OwnerDecision) => {
    try {
      setLoading(true);
      setError(null);

      const applicationRef = doc(db, 'applications', application.id);

      // Om ägaren avbryter ansökan helt, ta bort den
      if (decision === 'cancel_application') {
        await deleteDoc(applicationRef);
        setDecisionMade(decision);
        setTimeout(() => {
          if (onDecisionMade) {
            onDecisionMade(decision);
          }
          onClose();
        }, 2000);
        return;
      }

      const updateData: any = {
        'contractFlow.ownerDecision': decision,
        'contractFlow.decisionDate': new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Om ägaren väljer att skapa avtal
      if (decision === 'create_contract') {
        updateData['status'] = 'FINAL_APPROVED';
        updateData['contractFlow.status'] = 'contract_requested';
        updateData['contractFlow.requestedDate'] = new Date().toISOString();
        updateData['finalApprovedAt'] = new Date().toISOString();
      }
      // Om ägaren vill hitta ny hyresgäst - återgå till steg 2
      else if (decision === 'find_new_tenant') {
        updateData['status'] = 'APPROVED';
        updateData['step'] = 2;
        // Rensa hyresgästinfo så ägaren kan lägga till ny
        updateData['tenantFirstName'] = '';
        updateData['tenantLastName'] = '';
        updateData['tenantEmail'] = '';
        updateData['tenantPhoneNumber'] = '';
        updateData['tenantPersonalNumber'] = '';
        updateData['creditCheckResult'] = null;
        updateData['creditCheckAnswers'] = null;
        updateData['creditCheckPdfUrl'] = null;
        updateData['creditCheckComments'] = null;
        updateData['creditCheckDate'] = null;
        // Rensa contractFlow
        updateData['contractFlow'] = null;
      }

      await updateDoc(applicationRef, updateData);

      setDecisionMade(decision);

      // Vänta lite innan vi stänger för att visa bekräftelse
      setTimeout(() => {
        if (onDecisionMade) {
          onDecisionMade(decision);
        }
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Error saving owner decision:', error);
      setError('Ett fel uppstod när beslutet skulle sparas. Försök igen.');
      setLoading(false);
    }
  };

  // Om beslut är taget, visa bekräftelse
  if (decisionMade) {
    const confirmationConfig = {
      create_contract: {
        bgColor: 'bg-green-100',
        icon: <CheckCircle sx={{ fontSize: 48, color: '#22c55e' }} />,
        title: 'Avtal kommer att skapas!',
        message: 'Hyria har fått en begäran om att skapa hyresavtal. Du kommer att få ett mail när avtalet är klart för signering.'
      },
      find_new_tenant: {
        bgColor: 'bg-blue-100',
        icon: <CheckCircle sx={{ fontSize: 48, color: '#3b82f6' }} />,
        title: 'Du kan nu lägga till en ny hyresgäst',
        message: 'Ansökan har återgått till steg 2. Du kan nu lägga till en ny hyresgäst.'
      },
      cancel_application: {
        bgColor: 'bg-red-100',
        icon: <Cancel sx={{ fontSize: 48, color: '#ef4444' }} />,
        title: 'Ansökan har tagits bort',
        message: 'Ansökan har avbrutits och tagits bort från systemet.'
      }
    };

    const config = confirmationConfig[decisionMade];

    return (
      <Dialog open={open} className="fixed z-50 inset-0 flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-md mx-auto p-8 text-center">
          <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${config.bgColor}`}>
            {config.icon}
          </div>

          <h2 className="text-xl font-bold text-[#222222] font-['Poppins'] mb-2">
            {config.title}
          </h2>

          <p className="text-sm text-[#626262]">
            {config.message}
          </p>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={!loading ? onClose : undefined} className="fixed z-50 inset-0 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-lg w-full max-w-2xl mx-auto max-h-[90vh] flex flex-col">
        <button
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500 z-10"
          onClick={onClose}
          disabled={loading}
          aria-label="Stäng"
        >
          <Close className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Kompakt header */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
              <span className="inline-block mr-2 text-green-500">✓</span>
              Hyresgästen godkänd
            </h2>
            <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">
              Klar för beslut
            </span>
          </div>

          {/* Info och kreditresultat */}
          <div className="space-y-2">
            <p className="text-sm text-[#626262]">
              📍 {application.address} {application.apartmentNumber && `Lgh ${application.apartmentNumber}`}
            </p>
            <p className="text-sm text-[#626262]">
              📅 {application.contractStartDate && application.contractEndDate
                ? `${new Date(application.contractStartDate).toLocaleDateString('sv-SE')} – ${new Date(application.contractEndDate).toLocaleDateString('sv-SE')}`
                : 'Ej angivet'}
            </p>
            <p className="text-sm text-[#626262]">
              👤 {application.tenantFirstName} {application.tenantLastName}
            </p>
            {application.rentalFee && (
              <p className="text-sm text-[#626262]">
                💰 {application.rentalFee} kr/mån
                {application.deposit && ` | Deposition: ${application.deposit} kr`}
              </p>
            )}

            {/* Inline kreditresultat */}
            {application.creditCheckAnswers && (
              <div className="flex flex-wrap gap-2 pt-2">
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                  application.creditCheckAnswers.betalningsanmarkningar === 'ja'
                    ? 'bg-red-500 text-white'
                    : 'bg-green-500 text-white'
                }`}>
                  Betalningsanm: {application.creditCheckAnswers.betalningsanmarkningar === 'ja' ? 'JA' : 'NEJ'}
                </span>
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                  application.creditCheckAnswers.skuldsaldo === 'ja'
                    ? 'bg-red-500 text-white'
                    : 'bg-green-500 text-white'
                }`}>
                  Kronofogden: {application.creditCheckAnswers.skuldsaldo === 'ja' ? 'JA' : 'NEJ'}
                </span>
                <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${
                  application.creditCheckAnswers.inkomst === 'ja'
                    ? 'bg-green-500 text-white'
                    : 'bg-red-500 text-white'
                }`}>
                  Inkomst OK: {application.creditCheckAnswers.inkomst === 'ja' ? 'JA' : 'NEJ'}
                </span>
                {application.creditCheckPdfUrl && (
                  <button
                    onClick={() => window.open(application.creditCheckPdfUrl, '_blank')}
                    className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#62B8E9] text-white hover:bg-[#5ba3d4] transition-colors"
                  >
                    📄 Visa PDF
                  </button>
                )}
              </div>
            )}

            {application.creditCheckComments && (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs font-semibold text-[#626262] mb-1">💬 Kommentar från Hyria:</p>
                <p className="text-sm text-[#222222]">{application.creditCheckComments}</p>
              </div>
            )}

            {application.creditCheckDate && (
              <p className="text-xs text-[#626262] mt-2">
                Utförd: {new Date(application.creditCheckDate).toLocaleString('sv-SE')}
              </p>
            )}
          </div>

          {/* Informationsruta */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900 font-semibold mb-2">
              ℹ️ Vad händer när du väljer?
            </p>
            <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
              <li><strong>Skapa avtal</strong>: Hyria upprättar hyresavtal och skickar det för signering via BankID</li>
              <li><strong>Hitta ny hyresgäst</strong>: Återgå till steg 2 och lägg till en annan hyresgäst</li>
              <li><strong>Avbryt ansökan</strong>: Tar bort ansökan helt från systemet</li>
            </ul>
          </div>

          {/* Beslutsknappar */}
          <div className="border-t border-[#E2EAF0] pt-4 mt-4">
            <div className="flex gap-2">
              <button
                onClick={() => handleDecision('create_contract')}
                disabled={loading}
                className="flex-1 bg-green-500 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 text-sm"
              >
                {loading ? '⏳ Bearbetar...' : '✓ Skapa avtal'}
              </button>
              <button
                onClick={() => handleDecision('find_new_tenant')}
                disabled={loading}
                className="flex-1 bg-[#62B8E9] text-white font-bold py-2.5 px-4 rounded-lg hover:bg-[#5ba3d4] transition-colors disabled:opacity-50 text-sm"
              >
                🔄 Hitta ny hyresgäst
              </button>
            </div>
            <button
              onClick={() => handleDecision('cancel_application')}
              disabled={loading}
              className="w-full mt-2 bg-red-500 text-white font-bold py-2.5 px-4 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 text-sm"
            >
              ✗ Avbryt ansökan
            </button>
            {!loading && (
              <button
                onClick={onClose}
                className="w-full mt-2 text-[#626262] py-2 text-sm hover:text-[#222222] transition-colors"
              >
                Stäng och bestäm senare
              </button>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default OwnerDecisionModal;